"use strict";

exports.__esModule = true;
exports.MeshAttachment = void 0;

var _TextureAtlas = require("../TextureAtlas");

var _Utils = require("../Utils");

var _Attachment = require("./Attachment");

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

/** An attachment that displays a textured mesh. A mesh has hull vertices and internal vertices within the hull. Holes are not
 * supported. Each vertex has UVs (texture coordinates) and triangles are used to map an image on to the mesh.
 *
 * See [Mesh attachments](http://esotericsoftware.com/spine-meshes) in the Spine User Guide. */
var MeshAttachment = /*#__PURE__*/function (_VertexAttachment) {
  _inheritsLoose(MeshAttachment, _VertexAttachment);

  function MeshAttachment(name) {
    var _this;

    _this = _VertexAttachment.call(this, name) || this;
    _this.region = null;
    /** The name of the texture region for this attachment. */

    _this.path = null;
    /** The UV pair for each vertex, normalized within the texture region. */

    _this.regionUVs = null;
    /** The UV pair for each vertex, normalized within the entire texture.
     *
     * See {@link #updateUVs}. */

    _this.uvs = null;
    /** Triplets of vertex indices which describe the mesh's triangulation. */

    _this.triangles = null;
    /** The color to tint the mesh. */

    _this.color = new _Utils.Color(1, 1, 1, 1);
    /** The width of the mesh's image. Available only when nonessential data was exported. */

    _this.width = 0;
    /** The height of the mesh's image. Available only when nonessential data was exported. */

    _this.height = 0;
    /** The number of entries at the beginning of {@link #vertices} that make up the mesh hull. */

    _this.hullLength = 0;
    /** Vertex index pairs describing edges for controling triangulation. Mesh triangles will never cross edges. Only available if
     * nonessential data was exported. Triangulation is not performed at runtime. */

    _this.edges = null;
    _this.parentMesh = null;
    _this.tempColor = new _Utils.Color(0, 0, 0, 0);
    return _this;
  }
  /** Calculates {@link #uvs} using {@link #regionUVs} and the {@link #region}. Must be called after changing the region UVs or
   * region. */


  var _proto = MeshAttachment.prototype;

  _proto.updateUVs = function updateUVs() {
    var regionUVs = this.regionUVs;
    if (!this.uvs || this.uvs.length != regionUVs.length) this.uvs = _Utils.Utils.newFloatArray(regionUVs.length);
    var uvs = this.uvs;
    var n = this.uvs.length;
    var u = this.region.u,
        v = this.region.v,
        width = 0,
        height = 0;

    if (this.region instanceof _TextureAtlas.TextureAtlasRegion) {
      var region = this.region,
          page = region.page;
      var textureWidth = page.width,
          textureHeight = page.height;

      switch (region.degrees) {
        case 90:
          u -= (region.originalHeight - region.offsetY - region.height) / textureWidth;
          v -= (region.originalWidth - region.offsetX - region.width) / textureHeight;
          width = region.originalHeight / textureWidth;
          height = region.originalWidth / textureHeight;

          for (var i = 0; i < n; i += 2) {
            uvs[i] = u + regionUVs[i + 1] * width;
            uvs[i + 1] = v + (1 - regionUVs[i]) * height;
          }

          return;

        case 180:
          u -= (region.originalWidth - region.offsetX - region.width) / textureWidth;
          v -= region.offsetY / textureHeight;
          width = region.originalWidth / textureWidth;
          height = region.originalHeight / textureHeight;

          for (var _i = 0; _i < n; _i += 2) {
            uvs[_i] = u + (1 - regionUVs[_i]) * width;
            uvs[_i + 1] = v + (1 - regionUVs[_i + 1]) * height;
          }

          return;

        case 270:
          u -= region.offsetY / textureWidth;
          v -= region.offsetX / textureHeight;
          width = region.originalHeight / textureWidth;
          height = region.originalWidth / textureHeight;

          for (var _i2 = 0; _i2 < n; _i2 += 2) {
            uvs[_i2] = u + (1 - regionUVs[_i2 + 1]) * width;
            uvs[_i2 + 1] = v + regionUVs[_i2] * height;
          }

          return;
      }

      u -= region.offsetX / textureWidth;
      v -= (region.originalHeight - region.offsetY - region.height) / textureHeight;
      width = region.originalWidth / textureWidth;
      height = region.originalHeight / textureHeight;
    } else if (!this.region) {
      u = v = 0;
      width = height = 1;
    } else {
      width = this.region.u2 - u;
      height = this.region.v2 - v;
    }

    for (var _i3 = 0; _i3 < n; _i3 += 2) {
      uvs[_i3] = u + regionUVs[_i3] * width;
      uvs[_i3 + 1] = v + regionUVs[_i3 + 1] * height;
    }
  }
  /** The parent mesh if this is a linked mesh, else null. A linked mesh shares the {@link #bones}, {@link #vertices},
   * {@link #regionUVs}, {@link #triangles}, {@link #hullLength}, {@link #edges}, {@link #width}, and {@link #height} with the
   * parent mesh, but may have a different {@link #name} or {@link #path} (and therefore a different texture). */
  ;

  _proto.getParentMesh = function getParentMesh() {
    return this.parentMesh;
  }
  /** @param parentMesh May be null. */
  ;

  _proto.setParentMesh = function setParentMesh(parentMesh) {
    this.parentMesh = parentMesh;

    if (parentMesh) {
      this.bones = parentMesh.bones;
      this.vertices = parentMesh.vertices;
      this.worldVerticesLength = parentMesh.worldVerticesLength;
      this.regionUVs = parentMesh.regionUVs;
      this.triangles = parentMesh.triangles;
      this.hullLength = parentMesh.hullLength;
      this.worldVerticesLength = parentMesh.worldVerticesLength;
    }
  };

  _proto.copy = function copy() {
    if (this.parentMesh) return this.newLinkedMesh();
    var copy = new MeshAttachment(this.name);
    copy.region = this.region;
    copy.path = this.path;
    copy.color.setFromColor(this.color);
    this.copyTo(copy);
    copy.regionUVs = new Array(this.regionUVs.length);

    _Utils.Utils.arrayCopy(this.regionUVs, 0, copy.regionUVs, 0, this.regionUVs.length);

    copy.uvs = new Array(this.uvs.length);

    _Utils.Utils.arrayCopy(this.uvs, 0, copy.uvs, 0, this.uvs.length);

    copy.triangles = new Array(this.triangles.length);

    _Utils.Utils.arrayCopy(this.triangles, 0, copy.triangles, 0, this.triangles.length);

    copy.hullLength = this.hullLength; // Nonessential.

    if (this.edges) {
      copy.edges = new Array(this.edges.length);

      _Utils.Utils.arrayCopy(this.edges, 0, copy.edges, 0, this.edges.length);
    }

    copy.width = this.width;
    copy.height = this.height;
    return copy;
  }
  /** Returns a new mesh with the {@link #parentMesh} set to this mesh's parent mesh, if any, else to this mesh. **/
  ;

  _proto.newLinkedMesh = function newLinkedMesh() {
    var copy = new MeshAttachment(this.name);
    copy.region = this.region;
    copy.path = this.path;
    copy.color.setFromColor(this.color);
    copy.deformAttachment = this.deformAttachment;
    copy.setParentMesh(this.parentMesh ? this.parentMesh : this);
    copy.updateUVs();
    return copy;
  };

  return MeshAttachment;
}(_Attachment.VertexAttachment);

exports.MeshAttachment = MeshAttachment;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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